import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

import { AuthShell } from "@/components/auth/auth-shell";
import { SignInForm } from "@/components/auth/signin-form";
import { isAuthed, pb } from "@/lib/pb";
import { DEFAULT_BRAND, type TenantBrand, resolveTenantBrand } from "@/lib/tenant-brand";
import { useDocumentTitle } from "@/lib/use-document-title";

/**
 * Ported from portal-ui/app/(auth)/layout.tsx + signin/page.tsx.
 * Renders AuthShell (two-column hero + form) with the SPA's existing
 * pb.collection("auth").authWithPassword auth flow + ?next= round-trip.
 */
const searchSchema = z.object({ next: z.string().optional() });

function safeNext(next: string | undefined): string {
  if (!next) return "/dashboard";
  if (!next.startsWith("/")) return "/dashboard";
  if (next.startsWith("//")) return "/dashboard";
  return next;
}

export const Route = createFileRoute("/signin")({
  validateSearch: searchSchema,
  beforeLoad: ({ search }) => {
    if (isAuthed()) {
      throw redirect({ to: safeNext(search.next) as "/dashboard" });
    }
  },
  component: SignInPage,
});

function SignInPage() {
  useDocumentTitle("Sign in");
  const router = useRouter();
  const queryClient = useQueryClient();
  const { next } = Route.useSearch();

  // portal-ui resolves the tenant brand server-side from the request host.
  // We do the same on the client at mount — the SPA has no SSR.
  const [brand, setBrand] = useState<TenantBrand>(DEFAULT_BRAND);
  useEffect(() => {
    setBrand(resolveTenantBrand(window.location.host));
  }, []);

  async function handleSubmit(email: string, password: string) {
    await pb.collection("auth").authWithPassword(email, password);
    queryClient.clear();
    router.history.push(safeNext(next));
  }

  return (
    <AuthShell brand={brand}>
      <SignInForm onSubmit={handleSubmit} />
    </AuthShell>
  );
}
